import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DraggableModal from "./DraggableModal";
import StyledButton from "./StyledButton";
import theme from "../../helpers/theme";
import Grid from "@mui/material/Unstable_Grid2";

/**
 *
 * @param {String} modalTitle title text at top of modal
 * @param {Array.<Object>} fields JSON that determines how the edit modal is generated
 * (structure -> {key_in_object: {display_name: string, value: string}})
 * @param {Function} setFields setter for fields
 * @param {Boolean} modalVisible determines visibility of modal
 * @param {Function} handleCancelModal called when modal is canceled
 * @param {Function} handleSubmit called when modal submit button pressed
 * @returns a modal with text fields determined by an input array
 */
const EditObjectModal = (props) => {
  const [fieldComponents, setFieldComponents] = useState([]);
  const [placeholders, setPlaceholders] = useState({});
  const [activeEditing, setActiveEditing] = useState({});
  const [text, setText] = useState({});

  const toggleActiveEditing = (label) => {
    let newActiveEditing = { ...activeEditing };
    newActiveEditing[label] = !newActiveEditing[label];
    setActiveEditing(newActiveEditing);
  };

  const setTextValues = (event, label) => {
    event.preventDefault();
    let newFields = { ...props.fields };
    let thisField = newFields[label];
    thisField.value = event.target.value;
    event.stopPropagation();
    props.setFields(newFields);

    let newText = { ...text };
    newText[label] = event.target.value;
    setText(newText);
  };

  const handleEditClick = (event, label) => {
    event.stopPropagation();
    toggleActiveEditing(label);
  };

  const handleSaveClick = (event, label) => {
    event.stopPropagation();
    toggleActiveEditing(label);
    props.handleSubmit(event, label);
    let newPlaceholders = { ...placeholders };
    newPlaceholders[label] = props.fields[label].value;
    setPlaceholders(newPlaceholders);
  };

  const handleCancelClick = (event, label) => {
    event.stopPropagation();
    let newText = { ...text };
    newText[label] = placeholders[label];
    setText(newText);
    toggleActiveEditing(label);
  };

  // set activeEditing to false for all fields and set placeholders
  useEffect(() => {
    let editingState = {};
    let defaultPlaceholders = {};
    let defaultText = {};
    for (const [object_key, field] of Object.entries(props.fields)) {
      editingState[object_key] = false;
      defaultPlaceholders[object_key] = field.value;
      defaultText[object_key] = field.value;
    }
    setPlaceholders(defaultPlaceholders);
    setActiveEditing(editingState);
    setText(defaultText);
  }, []);

  useEffect(() => {
    if (!props.fields) {
      return;
    }
    let textFields = [];
    for (const [object_key, field] of Object.entries(props.fields)) {
      // use Grid instead of TableRow because grid items won't resize when making
      // elements visible
      textFields.push(
        <Grid
          key={object_key}
          container
          direction="row"
          spacing={1}
          sx={{ alignItems: "center" }}
        >
          <Grid sx={{ width: "25%" }}>
            <Typography align={"right"}>
              {field.display_name.replace(/_/g, " ")}
            </Typography>
          </Grid>
          <Grid sx={{ width: "45%", minWidth: "45%" }}>
            <TextField
              autoFocus
              margin="dense"
              value={text[object_key]}
              key={object_key}
              name={object_key}
              type="text"
              fullWidth
              variant="standard"
              onChange={(event) => setTextValues(event, object_key)}
              disabled={!activeEditing[object_key]}
            />
          </Grid>
          <Grid
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              gap: 1,
              paddingLeft: 3,
              width: "30%",
            }}
          >
            <StyledButton
              label={"Edit"}
              display={!activeEditing[object_key] ? "inline" : "none"}
              onClick={(event) => handleEditClick(event, object_key)}
              width={"50%"}
            />
            <StyledButton
              label={"Save"}
              display={activeEditing[object_key] ? "inline" : "none"}
              onClick={(event) => handleSaveClick(event, object_key)}
              width={"50%"}

            />
            <StyledButton
              label={"Cancel"}
              display={activeEditing[object_key] ? "inline" : "none"}
              customTheme="outlined"
              onClick={(event) => handleCancelClick(event, object_key)}
              width={"50%"}

            />
          </Grid>
        </Grid>
      );
    }
    setFieldComponents(textFields);
  }, [props.fields, activeEditing]);

  return (
    <DraggableModal
      modalVisible={props.modalVisible}
      handleCancelModal={props.handleCancelModal}
      modalTitle={props.modalTitle}
      content={<Grid>{fieldComponents}</Grid>}
    />
  );
};

export default EditObjectModal;
